// Baseball-themed assets for mobile auth
// MLB team data, sounds, patterns, and utilities

import { dynastyTokens } from '../../../services/colorService';

/**
 * MLB TEAM DATA
 * Full team info with abbreviations and official colors
 * 
 * NOTE: Team logos can be loaded from ESPN CDN:
 * https://a.espncdn.com/i/teamlogos/mlb/500/{abbrev}.png
 * For now, we use abbreviations with team colors
 */
export const mlbTeams = [
  // American League East
  { name: 'Baltimore Orioles', abbrev: 'BAL', primary: '#DF4601', secondary: '#000000' },
  { name: 'Boston Red Sox', abbrev: 'BOS', primary: '#BD3039', secondary: '#0C2340' },
  { name: 'New York Yankees', abbrev: 'NYY', primary: '#0C2340', secondary: '#C4CED4' },
  { name: 'Tampa Bay Rays', abbrev: 'TB', primary: '#092C5C', secondary: '#8FBCE6' },
  { name: 'Toronto Blue Jays', abbrev: 'TOR', primary: '#134A8E', secondary: '#1D2D5C' },
  
  // American League Central
  { name: 'Chicago White Sox', abbrev: 'CWS', primary: '#27251F', secondary: '#C4CED4' },
  { name: 'Cleveland Guardians', abbrev: 'CLE', primary: '#0C2340', secondary: '#E31937' },
  { name: 'Detroit Tigers', abbrev: 'DET', primary: '#0C2340', secondary: '#FA4616' },
  { name: 'Kansas City Royals', abbrev: 'KC', primary: '#004687', secondary: '#BD9B60' },
  { name: 'Minnesota Twins', abbrev: 'MIN', primary: '#002B5C', secondary: '#D31145' },
  
  // American League West
  { name: 'Houston Astros', abbrev: 'HOU', primary: '#002D62', secondary: '#EB6E1F' },
  { name: 'Los Angeles Angels', abbrev: 'LAA', primary: '#BA0021', secondary: '#003263' },
  { name: 'Oakland Athletics', abbrev: 'OAK', primary: '#003831', secondary: '#EFB21E' },
  { name: 'Seattle Mariners', abbrev: 'SEA', primary: '#0C2C56', secondary: '#005C5C' },
  { name: 'Texas Rangers', abbrev: 'TEX', primary: '#003278', secondary: '#C0111F' },
  
  // National League East
  { name: 'Atlanta Braves', abbrev: 'ATL', primary: '#CE1141', secondary: '#13274F' },
  { name: 'Miami Marlins', abbrev: 'MIA', primary: '#00A3E0', secondary: '#EF3340' },
  { name: 'New York Mets', abbrev: 'NYM', primary: '#002D72', secondary: '#FF5910' },
  { name: 'Philadelphia Phillies', abbrev: 'PHI', primary: '#E81828', secondary: '#002D72' },
  { name: 'Washington Nationals', abbrev: 'WSH', primary: '#AB0003', secondary: '#14225A' },
  
  // National League Central
  { name: 'Chicago Cubs', abbrev: 'CHC', primary: '#0E3386', secondary: '#CC3433' },
  { name: 'Cincinnati Reds', abbrev: 'CIN', primary: '#C6011F', secondary: '#000000' },
  { name: 'Milwaukee Brewers', abbrev: 'MIL', primary: '#12284B', secondary: '#FFC52F' },
  { name: 'Pittsburgh Pirates', abbrev: 'PIT', primary: '#27251F', secondary: '#FDB827' },
  { name: 'St. Louis Cardinals', abbrev: 'STL', primary: '#C41E3A', secondary: '#0C2340' },
  
  // National League West
  { name: 'Arizona Diamondbacks', abbrev: 'ARI', primary: '#A71930', secondary: '#E3D4AD' },
  { name: 'Colorado Rockies', abbrev: 'COL', primary: '#33006F', secondary: '#C4CED4' },
  { name: 'Los Angeles Dodgers', abbrev: 'LAD', primary: '#005A9C', secondary: '#EF3E42' },
  { name: 'San Diego Padres', abbrev: 'SD', primary: '#2F241D', secondary: '#FFC425' },
  { name: 'San Francisco Giants', abbrev: 'SF', primary: '#FD5A1E', secondary: '#27251F' }
];

/**
 * Get team by name
 */
export const getTeamByName = (name) => {
  return mlbTeams.find(team => team.name === name);
};

/**
 * Get team logo URL from ESPN CDN
 * TODO: Implement actual logo loading from ESPN or MLB Stats API
 */
export const getTeamLogoUrl = (abbrev) => {
  return `https://a.espncdn.com/i/teamlogos/mlb/500/${abbrev}.png`;
};

/**
 * BASEBALL BAT CRACK SOUND (Base64)
 * Short, crisp bat-on-ball sound effect
 * Duration: ~0.5 seconds
 */
export const batCrackSound = 'data:audio/mpeg;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAADhAC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7//////////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjEzAAAAAAAAAAAAAAAAJAAAAAAAAAAAA4S8VJ2QAAAAAAAAAAAAAAAAAAAAAAAA//sQZAAP8AAAaQAAAAgAAA0gAAABAAABpAAAACAAADSAAAAETEFNRTMuMTAwVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV//sQZDwAAADSAAAAAIAAANIAAAAAAAABpAAAACAAADSAAAAAVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV';

/**
 * Play bat crack sound effect
 */
export const playBatCrack = () => {
  try {
    const audio = new Audio(batCrackSound);
    audio.volume = 0.3; // 30% volume
    audio.play().catch(err => {
      console.log('Audio playback prevented by browser:', err);
    });
  } catch (error) {
    console.log('Could not play sound:', error);
  }
};

/**
 * Trigger device vibration
 * Pattern: [vibrate, pause, vibrate]
 */
export const triggerVibration = (pattern = [200]) => {
  if ('vibrate' in navigator) {
    navigator.vibrate(pattern);
  }
};

/**
 * SVG BASEBALL DIAMOND PATTERN
 * Subtle background pattern for auth screens
 * Dynamically uses primary color from dynastyTokens
 */
export const generateBaseballDiamondPattern = () => {
  const primaryColor = dynastyTokens.colors.primary;
  
  return `
    <svg width="400" height="400" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <pattern id="diamond-pattern" x="0" y="0" width="200" height="200" patternUnits="userSpaceOnUse">
          <!-- Baseball diamond (rotated square) -->
          <g opacity="0.03">
            <!-- Diamond outline -->
            <path d="M 100 20 L 180 100 L 100 180 L 20 100 Z" 
                  fill="none" 
                  stroke="${primaryColor}" 
                  stroke-width="2"/>
            
            <!-- Bases -->
            <rect x="95" y="15" width="10" height="10" fill="${primaryColor}"/> <!-- Home -->
            <rect x="175" y="95" width="10" height="10" fill="${primaryColor}"/> <!-- First -->
            <rect x="95" y="175" width="10" height="10" fill="${primaryColor}"/> <!-- Second -->
            <rect x="15" y="95" width="10" height="10" fill="${primaryColor}"/> <!-- Third -->
            
            <!-- Pitcher's mound -->
            <circle cx="100" cy="100" r="8" fill="none" stroke="${primaryColor}" stroke-width="1"/>
            <circle cx="100" cy="100" r="3" fill="${primaryColor}"/>
            
            <!-- Foul lines -->
            <line x1="100" y1="20" x2="20" y2="100" stroke="${primaryColor}" stroke-width="1" opacity="0.5"/>
            <line x1="100" y1="20" x2="180" y2="100" stroke="${primaryColor}" stroke-width="1" opacity="0.5"/>
          </g>
          
          <!-- Small baseballs scattered -->
          <g opacity="0.02">
            <circle cx="50" cy="50" r="8" fill="none" stroke="${primaryColor}" stroke-width="1"/>
            <path d="M 46 46 Q 50 48 54 46" stroke="${primaryColor}" stroke-width="0.5" fill="none"/>
            <path d="M 46 54 Q 50 52 54 54" stroke="${primaryColor}" stroke-width="0.5" fill="none"/>
            
            <circle cx="150" cy="150" r="8" fill="none" stroke="${primaryColor}" stroke-width="1"/>
            <path d="M 146 146 Q 150 148 154 146" stroke="${primaryColor}" stroke-width="0.5" fill="none"/>
            <path d="M 146 154 Q 150 152 154 154" stroke="${primaryColor}" stroke-width="0.5" fill="none"/>
          </g>
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#diamond-pattern)"/>
    </svg>
  `;
};

/**
 * Get background style with baseball pattern
 * Uses dynastyTokens primary color dynamically
 */
export const getBaseballBackground = () => {
  const pattern = generateBaseballDiamondPattern();
  const encodedSVG = encodeURIComponent(pattern);
  return {
    backgroundImage: `url("data:image/svg+xml,${encodedSVG}")`,
    backgroundRepeat: 'repeat',
    backgroundSize: '200px 200px'
  };
};

/**
 * SPINNING BASEBALL SVG
 * For loading animation
 */
export const SpinningBaseball = () => (
  <svg 
    className="animate-spin" 
    width="60" 
    height="60" 
    viewBox="0 0 60 60"
    xmlns="http://www.w3.org/2000/svg"
  >
    <defs>
      <radialGradient id="ballGradient">
        <stop offset="0%" stopColor="#ffffff" />
        <stop offset="100%" stopColor="#e7e7e7" />
      </radialGradient>
    </defs>
    
    {/* Baseball body */}
    <circle cx="30" cy="30" r="28" fill="url(#ballGradient)" stroke="#333" strokeWidth="1"/>
    
    {/* Red stitches */}
    <path 
      d="M 15 20 Q 20 15, 25 15 T 35 20" 
      fill="none" 
      stroke="#e74c3c" 
      strokeWidth="2" 
      strokeLinecap="round"
    />
    <path 
      d="M 25 45 Q 30 50, 35 50 T 45 45" 
      fill="none" 
      stroke="#e74c3c" 
      strokeWidth="2" 
      strokeLinecap="round"
    />
    
    {/* Stitch marks */}
    <line x1="17" y1="22" x2="19" y2="20" stroke="#e74c3c" strokeWidth="2" strokeLinecap="round"/>
    <line x1="21" y1="17" x2="23" y2="16" stroke="#e74c3c" strokeWidth="2" strokeLinecap="round"/>
    <line x1="27" y1="15" x2="29" y2="15" stroke="#e74c3c" strokeWidth="2" strokeLinecap="round"/>
    <line x1="33" y1="16" x2="35" y2="17" stroke="#e74c3c" strokeWidth="2" strokeLinecap="round"/>
    
    <line x1="27" y1="48" x2="29" y2="49" stroke="#e74c3c" strokeWidth="2" strokeLinecap="round"/>
    <line x1="33" y1="49" x2="35" y2="48" stroke="#e74c3c" strokeWidth="2" strokeLinecap="round"/>
    <line x1="39" y1="47" x2="41" y2="46" stroke="#e74c3c" strokeWidth="2" strokeLinecap="round"/>
    <line x1="43" y1="44" x2="44" y2="42" stroke="#e74c3c" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

/**
 * CONFETTI EMOJIS
 * Baseball-themed emojis for success celebration
 */
export const baseballEmojis = ['⚾', '🏆', '⚾', '🏟️', '⚾', '🎉', '⚾', '👑'];

/**
 * Generate random confetti piece
 */
export const generateConfettiPiece = () => ({
  emoji: baseballEmojis[Math.floor(Math.random() * baseballEmojis.length)],
  x: Math.random() * 100, // percent
  delay: Math.random() * 0.5, // seconds
  duration: 2 + Math.random() * 2, // 2-4 seconds
  rotation: Math.random() * 360 // degrees
});
